/**
 * The CSS string-literal rule, in one place.
 *
 * Two scanners in `emlAdapter` walk CSS a character at a time and must both ignore anything inside a
 * quoted `<string>`: `findMatchingParenthesis`, so the `)` in `url("a(b).png")` does not close the
 * function, and `dropRemoteCssStatements`, so a `;` inside a URL path is not a statement boundary.
 * What they layer on top differs — parenthesis depth against statement accumulation — but the quote
 * rule underneath is identical, and it was written out once in each.
 *
 * Two copies of a rule are two chances to correct only one. A backslash-escaped quote, a CSS comment:
 * either fix applied to one scanner and not the other leaves two passes that are meant to read the
 * same CSS the same way silently disagreeing about where its strings end. The rule lives here so a
 * correction lands once, and each scanner keeps only the part that is its own.
 */

/**
 * Tracks whether a character-at-a-time walk of CSS is currently inside a quoted `<string>`.
 *
 * Stateful and single-pass by design: it holds the open delimiter between calls, so one instance
 * belongs to one walk and is not reusable across two.
 */
export class CssQuoteTracker {
  /** The delimiter that opened the string being scanned; '' when the walk is outside one. */
  private quote = '';

  /**
   * Consumes one character and reports whether the caller's own logic should act on it.
   *
   * False covers a string's contents *and* the delimiters themselves. That is exactly the set both
   * callers already skipped: a quote character is never a parenthesis and never a statement
   * boundary, so ignoring the delimiters costs no caller anything and keeps the whole rule inside
   * this class rather than half here and half at each call site.
   *
   * Must be called for **every** character, in order — this call is what advances the state, so a
   * character passed over loses whatever string boundary it carried and desynchronises the walk.
   * @param {string} char - The next character of the CSS text.
   * @returns {boolean} True when the character lies outside any string and is not a delimiter.
   */
  accept(char: string): boolean {
    if (this.quote !== '') {
      if (char === this.quote) this.quote = '';
      return false;
    }
    if (char === '"' || char === "'") {
      this.quote = char;
      return false;
    }
    return true;
  }
}
